import React, { ChangeEvent, useState } from 'react';
import { Icon } from '@iconify/react';
import { userService } from '../../services/user.service';
import { showErrorMessage, showSuccessMessage } from '../../helper/showMessage';

interface ChangePasswordModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
}

type FormType = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export default function ChangePasswordModal({ visible, setVisible }: ChangePasswordModalProps): React.ReactElement {
    const [error, setError] = useState<{ [key: string]: string }>({});
    const [form, setForm] = useState<FormType>({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [isUpdating, setIsUpdating] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const resetForm = () => {
        setForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        });
        setError({});
        setShowPasswords({
            current: false,
            new: false,
            confirm: false
        });
    };

    const handleClose = () => {
        resetForm();
        setVisible(false);
    };

    async function ErrorHandler(data: FormType) {
        let err: { [key: string]: string } = {};
        
        if (!data.currentPassword || data.currentPassword.trim() === '') {
            err.currentPassword = 'Current password is required';
        }
        
        if (!data.newPassword || data.newPassword.trim() === '') {
            err.newPassword = 'New password is required';
        } else if (data.newPassword.length < 6) {
            err.newPassword = 'New password must be at least 6 characters';
        }
        
        if (!data.confirmPassword || data.confirmPassword.trim() === '') {
            err.confirmPassword = 'Please confirm your new password';
        } else if (data.newPassword !== data.confirmPassword) {
            err.confirmPassword = 'Passwords do not match';
        }
        
        if (data.currentPassword === data.newPassword) {
            err.newPassword = 'New password must be different from current password';
        }
        
        setError(err);
        return Object.keys(err).length > 0;
    }

    async function SubmitHandler(event: React.FormEvent) {
        event.preventDefault();

        const isValidated = await ErrorHandler(form);
        if (isValidated) return;

        try {
            setIsUpdating(true);
            await userService.changePassword({
                currentPassword: form.currentPassword,
                newPassword: form.newPassword
            });
            
            showSuccessMessage('Password changed successfully');
            handleClose();
        } catch (error: any) {
            showErrorMessage(error?.response?.data?.error || error.message || 'Failed to change password');
        } finally {
            setIsUpdating(false);
        }
    }

    if (!visible) return <></>;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            {isUpdating && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/20">
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-lg">
                        <Icon icon="eos-icons:loading" className="text-xl animate-spin text-green" />
                        <span className="font-[Geist-Medium] text-sm">Updating...</span>
                    </div>
                </div>
            )}
            
            <form onSubmit={SubmitHandler}>
                <div className="w-[90vw] md:w-[600px] lg:w-[600px] xl:w-[600px] max-h-[95vh] max-content bg-white rounded-lg shadow-lg p-6 flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <h2 className="font-[Geist-Bold] text-lg md:text-xl text-green">
                            Change Password
                        </h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <Icon icon="gridicons:cross" className="text-xl" />
                        </button>
                    </div>

                    {/* Form Content */}
                    <div className="flex-1 overflow-y-auto px-3 space-y-4">
                        {/* Current Password */}
                        <div className="space-y-2">
                            <label className="block font-[Geist-Medium] text-sm text-gray-700">
                                Current Password *
                            </label>
                            <div className="relative">
                                <input
                                    type={showPasswords.current ? "text" : "password"}
                                    value={form.currentPassword}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => 
                                        setForm(prev => ({ ...prev, currentPassword: event.target.value }))
                                    }
                                    className="w-full h-10 px-4 pr-12 border border-gray-3 rounded-lg focus:outline-none focus:border-green font-[Geist-Medium] transition-colors"
                                    placeholder="Enter current password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <Icon 
                                        icon={showPasswords.current ? "mdi:eye-off" : "mdi:eye"} 
                                        className="text-xl" 
                                    />
                                </button>
                            </div>
                            {error.currentPassword && (
                                <p className="text-red-500 text-xs font-[Geist-Medium]">{error.currentPassword}</p>
                            )}
                        </div>

                        {/* New Password */}
                        <div className="space-y-2">
                            <label className="block font-[Geist-Medium] text-sm text-gray-700">
                                New Password *
                            </label>
                            <div className="relative">
                                <input
                                    type={showPasswords.new ? "text" : "password"}
                                    value={form.newPassword}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => 
                                        setForm(prev => ({ ...prev, newPassword: event.target.value }))
                                    }
                                    className="w-full h-10 px-4 pr-12 border border-gray-3 rounded-lg focus:outline-none focus:border-green font-[Geist-Medium] transition-colors"
                                    placeholder="Enter new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <Icon 
                                        icon={showPasswords.new ? "mdi:eye-off" : "mdi:eye"} 
                                        className="text-xl" 
                                    />
                                </button>
                            </div>
                            {error.newPassword && (
                                <p className="text-red-500 text-xs font-[Geist-Medium]">{error.newPassword}</p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <label className="block font-[Geist-Medium] text-sm text-gray-700">
                                Confirm New Password *
                            </label>
                            <div className="relative">
                                <input
                                    type={showPasswords.confirm ? "text" : "password"}
                                    value={form.confirmPassword}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => 
                                        setForm(prev => ({ ...prev, confirmPassword: event.target.value }))
                                    }
                                    className="w-full h-10 px-4 pr-12 border border-gray-3 rounded-lg focus:outline-none focus:border-green font-[Geist-Medium] transition-colors"
                                    placeholder="Confirm new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <Icon 
                                        icon={showPasswords.confirm ? "mdi:eye-off" : "mdi:eye"} 
                                        className="text-xl" 
                                    />
                                </button>
                            </div>
                            {error.confirmPassword && (
                                <p className="text-red-500 text-xs font-[Geist-Medium]">{error.confirmPassword}</p>
                            )}
                        </div>

                        {/* Password Requirements */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-[Geist-Medium] text-sm text-blue-800 mb-2">Password Requirements:</h4>
                            <ul className="text-xs text-blue-700 space-y-1">
                                <li>• At least 6 characters long</li>
                                <li>• Must be different from current password</li>
                                <li>• Confirm password must match new password</li>
                            </ul>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center items-center gap-6 pt-5 border-t border-gray-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-40 h-12 bg-gray-3 hover:bg-gray-2 text-white rounded-lg transition-colors font-[Geist-Medium] text-sm border border-gray-3"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="w-40 h-12 bg-green hover:bg-green-600 disabled:opacity-50 text-white rounded-lg transition-colors font-[Geist-Medium] text-sm border border-green"
                        >
                            {isUpdating ? 'Updating...' : 'Change Password'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
